import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, message } from "antd";
import {
  beginPDFFinalization,
  cancelPDFFinalization,
  finalizePDF,
} from "../../service/uploadService";
import { getPresignedDocumentUrl } from "../Document/DocumentUtils";
import { reloadFinalizedPdf } from "./pdfFinalize";

const documentId = (record) => {
  if (record?.id != null) return String(record.id);
  const uniqueId = String(record?.unique_id || "");
  return uniqueId.startsWith("D") ? uniqueId.slice(1) : uniqueId || null;
};

const isPdfDocument = (record) => {
  const fileType = String(record?.file_type || "")
    .toLowerCase()
    .trim()
    .replace(/^\./, "");
  const extension = String(record?.name || "")
    .split(".")
    .pop()
    .toLowerCase();
  return (
    fileType === "pdf" ||
    fileType === "application/pdf" ||
    extension === "pdf"
  );
};

const FinalizePdfButton = ({
  instance,
  documentRecord,
  disabled = false,
  onFinalized,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const docId = useMemo(() => documentId(documentRecord), [documentRecord]);

  if (!isPdfDocument(documentRecord)) return null;

  const handleFinalize = async () => {
    if (!docId || !instance || finalizing) return;
    setFinalizing(true);
    let finalizationToken = null;
    try {
      const sessionResponse = await beginPDFFinalization(docId);
      finalizationToken = sessionResponse?.data?.finalization_token;
      if (!finalizationToken) {
        throw new Error(
          "The server did not start PDF finalization. Please try again."
        );
      }
      const response = await finalizePDF(docId, finalizationToken);
      finalizationToken = null;
      if (response?.data?.flattened !== true) {
        throw new Error(
          "The server saved the PDF without flattening it. Please try again."
        );
      }
      const savedDocument = response?.data?.document || null;
      const savedDocumentId = savedDocument?.id || docId;

      try {
        const {
          url: finalizedUrl,
          isDirect,
          withCredentials = false,
        } = await getPresignedDocumentUrl(savedDocumentId);
        await reloadFinalizedPdf(instance, finalizedUrl, {
          filename: savedDocument?.name || documentRecord?.name,
          streaming: !isDirect,
          withCredentials,
        });
      } catch {
        instance.Core?.annotationManager?.enableReadOnlyMode?.();
        onFinalized?.(savedDocument);
        message.error(
          "The PDF was finalized, but the viewer could not refresh. Reload this page to view the finalized version."
        );
        setConfirmOpen(false);
        return;
      }

      onFinalized?.(savedDocument);
      message.success(`Finalized ${documentRecord?.name || "PDF"}.`);
      setConfirmOpen(false);
    } catch (error) {
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Couldn't finalize the PDF. Please try again."
      );
    } finally {
      if (finalizationToken) {
        try {
          await cancelPDFFinalization(docId, finalizationToken);
        } catch {
          // The server may already have consumed or expired the session.
        }
      }
      setFinalizing(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setConfirmOpen(true)}
        disabled={disabled || !docId || !instance}
        style={{
          height: 30,
          padding: "0 12px",
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        Finalize
      </Button>
      <Modal
        open={confirmOpen}
        title="Finalize PDF"
        okText={finalizing ? "Finalizing…" : "Finalize"}
        cancelText="Cancel"
        onOk={handleFinalize}
        onCancel={() => !finalizing && setConfirmOpen(false)}
        okButtonProps={{ loading: finalizing }}
        cancelButtonProps={{ disabled: finalizing }}
        closable={!finalizing}
        maskClosable={!finalizing}
        destroyOnClose
      >
        <p>
          This replaces the current document with a new flattened version.
          Form fields, signatures, and annotations will become permanent and
          cannot be edited.
        </p>
      </Modal>
    </>
  );
};

FinalizePdfButton.propTypes = {
  instance: PropTypes.object,
  documentRecord: PropTypes.object,
  disabled: PropTypes.bool,
  onFinalized: PropTypes.func,
};

export default FinalizePdfButton;
